import { useRef, useState } from "react";

/**
 * Wraps an async `refetch` so it never overlaps itself: a trigger while idle
 * starts `refetch` immediately; triggers that arrive while a run is in flight
 * collapse into a single queued re-run that starts as soon as the current one
 * finishes. At most one request is ever running and at most one is pending —
 * every extra trigger in between is dropped, because the queued run will fetch
 * state at least as fresh as the event that asked for it.
 *
 * The in-flight round-trip itself is the coalescing window, so fresh data is
 * never delayed by an arbitrary timer: on a fast network refreshes stay
 * near-instant, on a slow one they self-pace to what the connection can carry.
 *
 * Built for WebSocket-driven list refetches: `visit:updated` events on the
 * global `rn:all` channel arrive every ~1–3 s for the whole duration of a
 * broadcast burst (the 2026-07-01 slow-load incident) — the previous trailing
 * 300 ms debounce merged nothing at that cadence and fired a full refetch per
 * event.
 */
export function createCoalescedRunner(refetch: () => Promise<unknown>): () => void {
    let running = false;
    let queued = false;

    const runOnce = (): void => {
        running = true;
        void Promise.resolve()
            .then(refetch)
            // A failed refetch must not wedge the runner; callers handle their
            // own errors (the loaders all .catch internally).
            .catch(() => {})
            .finally(() => {
                running = false;
                if (queued) {
                    queued = false;
                    runOnce();
                }
            });
    };

    return () => {
        if (running) {
            queued = true;
            return;
        }
        runOnce();
    };
}

/** React binding: a stable trigger that always runs the latest `refetch`. */
export function useCoalescedRefetch(refetch: () => Promise<unknown>): () => void {
    const refetchRef = useRef(refetch);
    refetchRef.current = refetch;
    const [trigger] = useState(() => createCoalescedRunner(() => refetchRef.current()));
    return trigger;
}
